import { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Grid, InputAdornment, IconButton } from "@mui/material";
import { Controller } from "react-hook-form";
import FieldError from "shared/organisms/dynamicform/FieldError";
import EventIcon from "@mui/icons-material/Event";
import {
  customDatePickerStyles,
  TextInput,
  headerWrapperStyle,
  headerRowStyle,
  navButtonStyle,
  headerLabelStyle,
  yearGridContainerStyle,
  yearGridStyle,
  yearButtonStyle,
  yearButtonActiveStyle,
} from "./style";

interface IDateRangePickerFieldProps {
  field: any;
  control: any;
  errors: any;
}

// SVG chevron — rotates smoothly open/closed
const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      transition: "transform 0.2s ease",
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      color: "rgba(0,0,0,0.54)",
    }}
  >
    <path
      d="M2 4L6 8L10 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const YearSelectHeader = ({
  date,
  changeYear,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}: any) => {
  const [showYearPicker, setShowYearPicker] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const activeYearRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (showYearPicker && activeYearRef.current && gridRef.current) {
      const grid = gridRef.current;
      const activeBtn = activeYearRef.current;
      const gridTop = grid.getBoundingClientRect().top;
      const btnTop = activeBtn.getBoundingClientRect().top;
      grid.scrollTop +=
        btnTop - gridTop - grid.clientHeight / 2 + activeBtn.clientHeight / 2;
    }
  }, [showYearPicker]);

  const currentYear = date.getFullYear();
  const startYear = 1999;
  const endYear = new Date().getFullYear() + 30;
  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i
  );

  const handleYearClick = (year: number) => {
    changeYear(year);
    setShowYearPicker(false);
  };

  return (
    <div style={headerWrapperStyle}>
      <div style={headerRowStyle}>
        {/* Previous month — plain text arrow */}
        <button
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          style={navButtonStyle}
          aria-label="Previous month"
        >
          ‹
        </button>

        {/* Month + Year label with animated SVG chevron */}
        <button
          onClick={() => setShowYearPicker((prev) => !prev)}
          style={headerLabelStyle}
          title="Click to select year"
        >
          {date.toLocaleString("default", { month: "long" })} {currentYear}
          
          <Chevron open={showYearPicker}/>
        </button>

        {/* Next month — plain text arrow */}
        <button
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          style={navButtonStyle}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      {showYearPicker && (
        <div ref={gridRef} style={yearGridContainerStyle}>
          <div style={yearGridStyle}>
            {years.map((year) => (
              <button
                key={year}
                ref={year === currentYear ? activeYearRef : null}
                onClick={() => handleYearClick(year)}
                style={{
                  ...yearButtonStyle,
                  ...(year === currentYear ? yearButtonActiveStyle : {}),
                }}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DateRangePickerField = ({
  field,
  control,
  errors,
}: IDateRangePickerFieldProps) => {
  return (
    <Grid item {...field.breakpoints}>
      <style>{customDatePickerStyles}</style>
      <Controller
        name={field.id}
        control={control}
        defaultValue={[null, null]}
        render={({ field: { onChange, value, onBlur } }) => {
          const [startDate, endDate] = Array.isArray(value)
            ? value
            : [null, null];
          return (
            <DatePicker
              selectsRange={true}
              startDate={startDate}
              endDate={endDate}
              dateFormat="dd/MM/yyyy"
              onChange={(update) => onChange(update)}
              onBlur={onBlur}
              renderCustomHeader={(headerProps) => (
                <YearSelectHeader {...headerProps} />
              )}
              customInput={
                <TextInput
                  $readOnly={field.readOnly}
                  sx={{ width: "100%" }}
                  {...{
                    error: !!(errors && errors[field.id]?.message),
                    helperText: null,
                  }}
                  defaultValue={""}
                  required={field.required}
                  size="small"
                  label={field.label}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end">
                          <EventIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                    readOnly: true,
                  }}
                />
              }
              isClearable={!field.readOnly}
              disabled={field.disabled}
              placeholderText={field.placeholder || "Select date range"}
            />
          );
        }}
      />
      {errors && errors[field.id]?.message && (
        <FieldError field={field} errors={errors} />
      )}
    </Grid>
  );
};

export default DateRangePickerField;